/**
 * Single source of truth for brand, mail-service and account configuration.
 *
 *   .env                 -> deployment values (name, domain, hosts, ports, social)
 *   site.manifest.json   -> static business facts (legal entity, address, support hours)
 *   navigation.ts        -> menus and footer link columns
 *
 * Import `site` anywhere (server or client). Never read `process.env` elsewhere.
 * NEXT_PUBLIC_* values are inlined at build time, so they work in client code too.
 */
import manifest from "./site.manifest.json";

const list = (value: string | undefined, fallback: string[]): string[] => {
  const items = (value ?? "")
    .split(/[,/]/)
    .map((s) => s.trim())
    .filter(Boolean);
  return items.length ? items : fallback;
};

/** [25, 587, 2525], "or" -> "25, 587 or 2525" */
const joinWords = (items: Array<string | number>, word: "and" | "or"): string =>
  items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} ${word} ${items[items.length - 1]}`;

const name = process.env.NEXT_PUBLIC_SITE_NAME || "SMTP-X";
const domain = process.env.NEXT_PUBLIC_SITE_DOMAIN || "smtp-x.com";
const url = (process.env.NEXT_PUBLIC_SITE_URL || `https://${domain}`).replace(/\/+$/, "");
const accountPath = `/${(process.env.NEXT_PUBLIC_ACCOUNT_PATH || "/account").replace(/^\/+|\/+$/g, "")}`;

const ports = list(process.env.NEXT_PUBLIC_SMTP_PORTS, ["25", "587", "2525"]).map(Number);
const primaryPort = Number(process.env.NEXT_PUBLIC_SMTP_PORT_PRIMARY) || ports.find((p) => p === 587) || ports[0];

const addressLabel = (process.env.NEXT_PUBLIC_SMTP_ADDRESS || "Dedicated/Shared IPv4").replace(/\s*\/\s*/g, " / ");
const regions = list(process.env.NEXT_PUBLIC_REGIONS, ["Berlin", "Riga", "Hong Kong"]);

const social = [
  { id: "facebook", label: "Facebook", href: process.env.NEXT_PUBLIC_SOCIAL_FACEBOOK },
  { id: "x", label: "X", href: process.env.NEXT_PUBLIC_SOCIAL_X },
  { id: "linkedin", label: "LinkedIn", href: process.env.NEXT_PUBLIC_SOCIAL_LINKEDIN },
  { id: "youtube", label: "YouTube", href: process.env.NEXT_PUBLIC_SOCIAL_YOUTUBE },
].filter((s): s is { id: string; label: string; href: string } => Boolean(s.href));

const logoVariant = (process.env.NEXT_PUBLIC_LOGO_VARIANT || "a").toLowerCase();

const mailbox = (local: string) => `${local}@${domain}`;

export const site = {
  name,
  domain,
  url,
  tagline: manifest.tagline,

  company: {
    legalName: manifest.company.legalName,
    address: manifest.company.address,
    phone: manifest.company.phone,
    phoneDisplay: manifest.company.phoneDisplay,
  },

  support: {
    hours: manifest.support.hours,
    salesEmail: mailbox(manifest.support.mailboxes.sales),
    supportEmail: mailbox(manifest.support.mailboxes.support),
  },

  smtp: {
    host: process.env.NEXT_PUBLIC_SMTP_HOST || `smtp.${domain}`,
    ports,
    primaryPort,
    /** "25 · 587 · 2525" */
    portsLabel: ports.join(" \u00b7 "),
    /** Ports other than the recommended one, e.g. [25, 2525] */
    otherPorts: ports.filter((p) => p !== primaryPort),
    /** "25, 587 or 2525" */
    portsOr: joinWords(ports, "or"),
    /** "25, 587 and 2525" */
    portsAnd: joinWords(ports, "and"),
    /** "25 and 2525" (everything except the recommended port) */
    otherPortsAnd: joinWords(
      ports.filter((p) => p !== primaryPort),
      "and",
    ),
    security: process.env.NEXT_PUBLIC_SMTP_SECURITY || "STARTTLS",
    /** Display string: "Dedicated / Shared IPv4" */
    addressLabel,
  },

  mailTester: {
    /** Receiving domain for generated test addresses (env). */
    domain: process.env.NEXT_PUBLIC_MAILTEST_DOMAIN || "mail.testmailscore.com",
    /** Where a batch's X-Tms-ID can be watched: `${inboxUrl}/<id>` (env). */
    inboxUrl: (process.env.NEXT_PUBLIC_MAILTEST_INBOX_URL || "https://testmailscore.com/email-tools/tms-inbox").replace(/\/+$/, ""),
    localPrefix: manifest.mailTester.localPrefix,
    idLength: manifest.mailTester.idLength,
    idAlphabet: manifest.mailTester.idAlphabet,
  },

  regions,
  /** "Berlin · Riga · Hong Kong" */
  regionsLabel: regions.join(" \u00b7 "),

  pricing: manifest.pricing,

  social,
  logoVariant,

  /**
   * The account area is a separate project served under this path.
   * Use `site.account("signup")` => "/account/signup".
   */
  accountPath,
  account(path = ""): string {
    const clean = path.replace(/^\/+/, "");
    return clean ? `${accountPath}/${clean}` : accountPath;
  },

  /** Absolute URL for a route on this site. */
  absolute(path = "/"): string {
    return path === "/" ? `${url}/` : `${url}${path.startsWith("/") ? path : `/${path}`}`;
  },
} as const;

export type Site = typeof site;
